import { clamp } from "@/shared/math/clamp";
import { type Message } from "@/shared/reader";

import { type IndexRange } from "./messaging/commands";

/**
 * Baseline number of messages in a single prefetch window.
 * `bucketize` scales this up by the clamped playback rate so that faster playback
 * prefetches further ahead, and `coalesceRanges` uses it as the upper bound when merging adjacent gaps.
 * Larger values amortize per-request overhead at the cost of fetching messages the user may never view.
 */
const DEFAULT_BATCH_SIZE = 200;

/**
 * Clamp range for the playback-rate multiplier applied to the bucket size in
 * `bucketize`. The lower bound keeps the prefetch window at full batch size
 * when the user pauses or scrubs (where `playbackRate` may be 0 or fractional);
 * the upper bound caps the worst-case fetch depth so a single poll cycle
 * cannot request a runaway range.
 *
 * `MAX_PLAYBACK_RATE` is also load-bearing for `FORWARD_KEEP_INDICES`: the
 * forward keep-window is sized against this same upper bound so `trimCache`
 * never evicts a range that `bucketize` just fetched.
 */
const MIN_PLAYBACK_RATE = 1;
const MAX_PLAYBACK_RATE = 2.5;

/**
 * Indices `trimCache` keeps ahead of the playhead in the direction of travel.
 *
 * Sized to cover the worst-case forward lookahead from `bucketize`: at the
 * upper rate clamp, bucket size is `DEFAULT_BATCH_SIZE × MAX_PLAYBACK_RATE`,
 * and bucket-extension can place the playhead anywhere in the lower half of
 * a two-bucket span. Doubling that size rounds the worst case up for headroom
 * and absorbs drift between the poll cycle that fetched a range and the one
 * that decides whether to keep it.
 */
const FORWARD_KEEP_INDICES = 2 * DEFAULT_BATCH_SIZE * MAX_PLAYBACK_RATE;

/**
 * Indices `trimCache` keeps behind the playhead in the direction of travel.
 *
 * One bucket's worth — enough to absorb a brief reverse-arrow tap without
 * re-fetching, while keeping the steady-state cache footprint close to the
 * forward keep window. The default `maxCacheSize` is the sum of this value
 * and `FORWARD_KEEP_INDICES`, which is also the trim trigger threshold.
 */
const BACKWARD_SCRUB_INDICES = DEFAULT_BATCH_SIZE;

export enum PlaybackDirection {
  Forward = "forward",
  Backward = "backward",
}

/**
 * Main-thread-side cache of decoded messages for a single Raw Message panel.
 *
 * Backs the panel's read path: the manager asks the cache which indices are
 * missing, requests them from the worker, then reads back through `indexToMessage`
 * to drive rendering. Two parallel arrays (`timestamps`, `sequences`) form the
 * time index; messages live in `messageCache` keyed by sequence number, with a
 * sidecar map (`sequenceToTimeAddedToCacheMap`) recording per-entry insertion
 * time so entries can be aged out and re-fetched.
 *
 * Staleness follows a "serve old data while we refresh" pattern (stale-while-revalidate):
 * an entry whose insertion timestamp is older than `staleTime` is still returned by `indexToMessage`
 * so playback never blanks out, but `getIndicesForMissingData` reports it as missing so the manager
 * re-fetches it.
 */
export class RawMessageCache {
  private backwardScrub: number;
  private batchSize: number;
  private forwardKeep: number;
  private maxCacheSize: number;
  /**
   * Cached messages keyed by sequence number. The Map instance is never
   * reassigned; all updates mutate it in place so external references stay
   * valid.
   */
  private messageCache = new Map<number, Message>();
  /** Parallel array of sequence numbers (transferred from worker) */
  private sequences: Uint32Array = new Uint32Array(0);
  /**
   * Insertion timestamp for each cached sequence, used by
   * `isCacheStaleAtSequence` to age entries out. Kept as a sidecar to
   * `messageCache` so `markCacheStale` can invalidate every entry by clearing
   * this map alone — the messages themselves stay readable while the manager
   * re-fetches.
   */
  private sequenceToTimeAddedToCacheMap = new Map<
    number,
    DOMHighResTimeStamp
  >();
  private staleTime: number;
  /** Sorted timestamps from the time index (transferred from worker) */
  private timestamps: BigUint64Array = new BigUint64Array(0);

  constructor(options?: {
    backwardScrub?: number;
    batchSize?: number;
    forwardKeep?: number;
    maxCacheSize?: number;
    staleTime?: number;
  }) {
    this.batchSize = options?.batchSize ?? DEFAULT_BATCH_SIZE;
    this.forwardKeep = options?.forwardKeep ?? FORWARD_KEEP_INDICES;
    this.backwardScrub = options?.backwardScrub ?? BACKWARD_SCRUB_INDICES;
    this.maxCacheSize =
      options?.maxCacheSize ?? this.forwardKeep + this.backwardScrub;
    this.staleTime = options?.staleTime ?? Number.POSITIVE_INFINITY;
  }

  /** Total number of entries in the time index */
  get length(): number {
    return this.timestamps.length;
  }

  /**
   * Add a batch of messages to the cache, keyed by sequence.
   *
   * Every write to `messageCache` must also record an insertion time in
   * `sequenceToTimeAddedToCacheMap`; an entry without a recorded insertion
   * time is treated as stale by `isCacheStaleAtSequence`, which would cause
   * the manager to re-fetch it on every poll cycle.
   */
  addMessages(messages: Message[]): void {
    const now = performance.now() + performance.timeOrigin;
    for (const msg of messages) {
      this.messageCache.set(msg.sequence, msg);
      this.sequenceToTimeAddedToCacheMap.set(msg.sequence, now);
    }
  }

  /**
   * Expand a target index into a bucket-aligned fetch region, scaled by
   * playback rate and biased in the given direction.
   *
   * Uses bucket-number alignment so that successive nearby indices tend
   * to land in the same bucket, producing cache hits at the boundary
   * instead of shifting the window on every tick.
   */
  bucketize(
    targetIndex: number,
    direction: PlaybackDirection = PlaybackDirection.Forward,
    playbackRate = 1,
  ): IndexRange {
    const size = Math.ceil(
      this.batchSize *
        clamp(playbackRate, MIN_PLAYBACK_RATE, MAX_PLAYBACK_RATE),
    );

    const currentBucket = Math.floor(targetIndex / size);

    // If the target is in the far half of its bucket relative to the
    // direction of travel, extend into the adjacent bucket so that
    // prefetch stays ahead of playback.
    const bucketMidpoint = currentBucket * size + size / 2;
    const shouldExtend =
      direction === PlaybackDirection.Forward
        ? targetIndex >= bucketMidpoint
        : targetIndex <= bucketMidpoint;

    const directionSign = direction === PlaybackDirection.Forward ? 1 : -1;
    const adjacentBucket = shouldExtend
      ? currentBucket + directionSign
      : currentBucket;

    const bucketStart = Math.min(currentBucket, adjacentBucket) * size;
    const bucketEnd = Math.max(currentBucket, adjacentBucket) * size + size - 1;

    const start = Math.max(0, bucketStart);
    const end = Math.min(this.timestamps.length - 1, bucketEnd);
    return { start, end };
  }

  /** Clear the time index and cache */
  clear(): void {
    this.timestamps = new BigUint64Array(0);
    this.sequences = new Uint32Array(0);
    this.messageCache.clear();
    this.sequenceToTimeAddedToCacheMap.clear();
  }

  /**
   * Walk the bucketized range and return all uncached sub-ranges.
   */
  getIndicesForMissingData(
    targetIndex: number,
    direction: PlaybackDirection = PlaybackDirection.Forward,
    playbackRate = 1,
  ): { missing: IndexRange[]; isTargetMissing: boolean } {
    if (this.timestamps.length === 0) {
      return { missing: [], isTargetMissing: false };
    }

    const { start, end } = this.bucketize(targetIndex, direction, playbackRate);

    const missing: IndexRange[] = [];
    let rangeStart: number | null = null;
    let isTargetMissing = false;

    for (let i = start; i <= end; i++) {
      const sequence = this.sequences[i];
      const isMissing =
        !this.messageCache.has(sequence) ||
        this.isCacheStaleAtSequence(sequence);

      if (isMissing) {
        if (i === targetIndex) {
          isTargetMissing = true;
        }
        if (rangeStart === null) {
          rangeStart = i;
        }
      } else if (rangeStart !== null) {
        missing.push({ start: rangeStart, end: i - 1 });
        rangeStart = null;
      }
    }
    if (rangeStart !== null) {
      missing.push({ start: rangeStart, end });
    }

    return { missing: this.coalesceRanges(missing), isTargetMissing };
  }

  /**
   * Look up a cached message by index position. Stale entries are still
   * returned so the panel keeps rendering the last-known message while the
   * worker re-fetches; `getIndicesForMissingData` reports the same entries as
   * missing in parallel so a refresh is queued.
   */
  indexToMessage(index: number): Message | undefined {
    if (index < 0 || index >= this.sequences.length) {
      return undefined;
    }
    const sequence = this.sequences[index];
    return this.messageCache.get(sequence);
  }

  /**
   * Mark every cached message as stale without discarding it. Callers
   * (currently `RawMessageManager.setState` on a same-topic update) use this
   * to force a refresh while keeping the panel rendering the previous frame:
   * `indexToMessage` still returns the existing entries, but
   * `getIndicesForMissingData` reports them as missing so the manager
   * re-fetches.
   */
  markCacheStale(): void {
    this.sequenceToTimeAddedToCacheMap.clear();
  }

  /** Set the time index arrays transferred from the worker */
  setTimestampIndex(timestamps: BigUint64Array, sequences: Uint32Array): void {
    if (timestamps.length !== sequences.length) {
      throw new Error(
        `Timestamp and sequence arrays must have the same length, got ${timestamps.length} and ${sequences.length}`,
      );
    }

    this.timestamps = timestamps;
    this.sequences = sequences;
    this.messageCache.clear();
    this.sequenceToTimeAddedToCacheMap.clear();
  }

  /**
   * Binary search for the latest index whose timestamp <= targetTime.
   * Returns -1 if no entry qualifies (target is before all entries).
   */
  timestampToIndex(targetTime: bigint): number {
    const timestamps = this.timestamps;
    let lo = 0;
    let hi = timestamps.length - 1;
    let result = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1;
      if (timestamps[mid] <= targetTime) {
        result = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return result;
  }

  /**
   * Trim the cache to stay within `maxCacheSize`, evicting entries farthest
   * from `targetIndex`.
   *
   * The keep-window is asymmetric and biased by `direction`. The wider half
   * (`forwardKeep`) extends in the direction of travel and must cover the
   * worst-case lookahead from `bucketize` so a range fetched on one poll
   * cycle is not evicted before the next emit reads it. The narrower half
   * (`backwardScrub`) covers short reverse seeks at the playhead's current
   * position.
   *
   * `direction` defaults to `PlaybackDirection.Forward` to match
   * `RawMessageManager.loadMessages` on its first poll cycle, when no prior
   * playhead is available to infer direction from.
   */
  trimCache(
    targetIndex: number,
    direction: PlaybackDirection = PlaybackDirection.Forward,
  ): void {
    if (this.messageCache.size <= this.maxCacheSize) {
      return;
    }

    const [keepBehind, keepAhead] =
      direction === PlaybackDirection.Forward
        ? [this.backwardScrub, this.forwardKeep]
        : [this.forwardKeep, this.backwardScrub];

    const windowStart = Math.max(0, targetIndex - keepBehind);
    const windowEnd = Math.min(
      (this.timestamps?.length ?? 0) - 1,
      targetIndex + keepAhead,
    );

    const keepSequences = new Set<number>();
    for (let i = windowStart; i <= windowEnd; i++) {
      const seq = this.sequences?.[i];
      if (seq !== undefined) {
        keepSequences.add(seq);
      }
    }

    for (const seq of this.messageCache.keys()) {
      if (!keepSequences.has(seq)) {
        this.clearCacheAtSequence(seq);
      }
    }
  }

  private clearCacheAtSequence(sequence: number): void {
    this.messageCache.delete(sequence);
    this.sequenceToTimeAddedToCacheMap.delete(sequence);
  }

  /**
   * Merge adjacent gaps when the space between them is small AND the
   * merged range stays within batchSize, preventing a chain of small
   * gaps from ballooning into a single oversized fetch.
   */
  private coalesceRanges(ranges: IndexRange[]): IndexRange[] {
    if (ranges.length <= 1) {
      return ranges;
    }
    const maxGap = Math.floor(this.batchSize / 4);
    const result: IndexRange[] = [{ ...ranges[0] }];
    for (let i = 1; i < ranges.length; i++) {
      const current = ranges[i];
      const last = result[result.length - 1];
      const gap = current.start - last.end - 1;
      const mergedSize = current.end - last.start + 1;
      if (gap <= maxGap && mergedSize <= this.batchSize) {
        last.end = current.end;
      } else {
        result.push({ ...current });
      }
    }
    return result;
  }

  private isCacheStaleAtSequence(sequence: number): boolean {
    const cachedAt = this.sequenceToTimeAddedToCacheMap.get(sequence) ?? -1;
    if (cachedAt === -1) {
      return true;
    }
    const now = performance.now() + performance.timeOrigin;
    return now - cachedAt > this.staleTime;
  }
}
